package com.sddamico.swigmore.pages

import androidx.compose.runtime.Composable
import com.sddamico.swigmore.components.layouts.ContentColumn
import com.sddamico.swigmore.components.layouts.SignedInPage
import com.sddamico.swigmore.ds.Reveal
import com.sddamico.swigmore.ds.core.PageTitle
import com.sddamico.swigmore.ds.css
import com.sddamico.swigmore.ds.feedback.PageTitleSkeleton
import com.sddamico.swigmore.ds.feedback.Skeleton
import com.sddamico.swigmore.ds.feedback.SkeletonBlock
import com.sddamico.swigmore.ds.forms.Button
import com.sddamico.swigmore.ds.forms.ButtonStyle
import com.sddamico.swigmore.ds.forms.ButtonVariant
import com.sddamico.swigmore.ds.poll.RankHistoryRow
import com.sddamico.swigmore.ds.poll.RankHistoryTable
import com.sddamico.swigmore.ds.poll.WeekCard
import com.sddamico.swigmore.models.PollArchiveResponse
import com.sddamico.swigmore.models.PollRules
import com.sddamico.swigmore.models.PollStanding
import com.sddamico.swigmore.util.AppTab
import com.sddamico.swigmore.util.CachedResource
import com.sddamico.swigmore.util.LoadState
import com.sddamico.swigmore.util.Routes
import com.sddamico.swigmore.util.firstPlaceVotes
import com.sddamico.swigmore.util.monthDay
import com.sddamico.swigmore.util.rememberCachedResource
import com.sddamico.swigmore.util.seasonParam
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import org.jetbrains.compose.web.dom.Div

/** The Record Book: each published week's No. 1 and the season's rank history. `?season=` picks the season. */
@Page
@Composable
fun ArchivePage() {
  val ctx = rememberPageContext()
  SignedInPage("Record Book", activeTab = AppTab.Archive) {
    val requested = seasonParam(ctx.route.params["season"])
    ContentColumn {
      requested.onFailure { PageTitle("Record Book", "The Record Book", lede = it.message) }
      requested.onSuccess { season ->
        when (val archive = rememberCachedResource(CachedResource.Archive(season)).state) {
          LoadState.Loading -> RecordBookSkeleton()
          is LoadState.Failed -> Reveal { PageTitle("Record Book", "The Record Book", lede = archive.message) }
          is LoadState.Ready -> Reveal { RecordBook(archive.value) { ctx.router.navigateTo(Routes.archive(it)) } }
        }
      }
    }
  }
}

@Composable
private fun RecordBook(archive: PollArchiveResponse, onSeason: (Int) -> Unit) {
  val weeks = archive.weeks
  val released = if (weeks.size == 1) "1 poll released" else "${weeks.size} polls released"
  PageTitle(
    eyebrow = listOfNotNull(archive.season?.let { "$it season" }, released).joinToString(" · "),
    title = "The Record Book",
    lede = if (weeks.isEmpty()) "Each week's No. 1 and the rank history land here once the first poll is published." else null,
  )
  if (archive.seasons.size > 1) {
    Div(attrs = Modifier.css("display" to "flex", "flex-wrap" to "wrap", "gap" to "8px", "margin-top" to "16px").toAttrs()) {
      archive.seasons.forEach { season ->
        Button(season.toString(), variant = if (season == archive.season) ButtonVariant.Cream else ButtonVariant.Outline,
          style = ButtonStyle.CapsSm, onDark = true, onClick = { onSeason(season) })
      }
    }
  }
  if (weeks.isEmpty()) return

  Div(
    attrs = Modifier.css("display" to "grid", "grid-template-columns" to "repeat(auto-fit,minmax(240px,1fr))",
      "gap" to "12px", "margin" to "24px 0 28px").toAttrs()
  ) {
    weeks.asReversed().forEach { results ->
      val leaders = results.ranked.filter { it.rank == 1 }
      val leader = leaders.firstOrNull()
      val note = when {
        leader == null -> "No ballots cast"
        results === weeks.first() -> "Inaugural poll · ${firstPlaceVotes(leader.firstPlaceVotes)}"
        else -> "${firstPlaceVotes(leader.firstPlaceVotes)} · ${leader.points} pts"
      }
      WeekCard(results.week.week, monthDay(checkNotNull(results.week.publishedAt)), leaders.joinToString(" / ") { it.team.name }.ifEmpty { "—" }, note)
    }
  }

  RankHistoryTable(
    weekLabels = weeks.map { "Wk ${it.week.week}" },
    rows = archive.history
      .map { team -> team.team.name to team.standings }
      .sortedWith { a, b -> compareLatestFirst(a.second, b.second) }
      .map { (name, standings) -> RankHistoryRow(name, standings) },
  )
}

/** Orders rows by the latest week's standing, falling back to earlier weeks, as the design does. */
private fun compareLatestFirst(a: List<PollStanding>, b: List<PollStanding>): Int {
  fun PollStanding.order() = when (this) {
    is PollStanding.Ranked -> rank
    PollStanding.ReceivingVotes -> PollRules.BALLOT_SIZE + 1
    PollStanding.Unranked -> PollRules.BALLOT_SIZE + 2
  }
  for (i in a.indices.reversed()) {
    val diff = a[i].order() - b[i].order()
    if (diff != 0) return diff
  }
  return 0
}

@Composable
private fun RecordBookSkeleton() {
  Skeleton {
    PageTitleSkeleton(lede = false)
    Div(
      attrs = Modifier.css("display" to "grid", "grid-template-columns" to "repeat(auto-fit,minmax(240px,1fr))",
        "gap" to "12px", "margin" to "24px 0 28px").toAttrs()
    ) {
      repeat(3) { SkeletonBlock("120px") }
    }
    SkeletonBlock("420px")
  }
}
